import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';

// project import
import { activeItem } from 'store/reducers/menu';
import { getTextColor } from 'utils/colorUtils';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const { drawerOpen, openItem } = useSelector((state) => state.menu);

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch(activeItem({ openItem: [id] }));
    };

    const isSelected = openItem.findIndex((id) => id === item.id) > -1;

    useEffect(() => {
        // Use exact URL matching only
        if (pathname === item.url) {
            dispatch(activeItem({ openItem: [item.id] }));
        }
    }, [pathname]);

    const textColor = 'text.primary';
    const iconSelectedColor = theme.palette.secondary.main;
    const iconColor = theme.palette.secondary.light;
    //const hoverColor = theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.lighter;
    const hoverColor = theme.palette.secondary.lighter;

    const Icon = item.icon;
    const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem', color: isSelected ? iconSelectedColor : iconColor }} /> : null;

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            onClick={() => itemHandler(item.id)}
            selected={isSelected}
            sx={{
                zIndex: 1201,
                pl: drawerOpen ? `${level * 28}px` : 1.5,
                py: !drawerOpen && level === 1 ? 1.25 : 1,
                marginLeft: 1,
                marginRight: 1,
                borderRadius: theme.borderRadius.sm,
                ...(drawerOpen && {
                    '&:hover': {
                        bgcolor: hoverColor,
                        '& .MuiTypography-root': {
                            color: getTextColor(hoverColor),
                        },
                    },
                    '&.Mui-selected': {
                        bgcolor: hoverColor,
                        color: iconSelectedColor,
                        '&:hover': {
                            color: iconSelectedColor,
                            bgcolor: hoverColor
                        }
                    }
                }),
                ...(!drawerOpen && {
                    '&:hover': {
                        bgcolor: 'transparent'
                    },
                    '&.Mui-selected': {
                        '&:hover': {
                            bgcolor: 'transparent'
                        },
                        bgcolor: 'transparent'
                    }
                })
            }}
        >
            {itemIcon && (
                <Tooltip title={item.title} placement="right" arrow>
                    <ListItemIcon
                        sx={{
                            minWidth: 28,
                            color: isSelected ? iconSelectedColor : textColor,
                            ...(!drawerOpen && {
                                borderRadius: theme.borderRadius.sm,
                                width: 36,
                                height: 36,
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    bgcolor: hoverColor,

                                }
                            }),
                            ...(!drawerOpen &&
                                isSelected && {
                                color: iconSelectedColor,
                                bgcolor: hoverColor,
                                '&:hover': {
                                    bgcolor: hoverColor

                                }
                            })
                        }}
                    >
                        {itemIcon}
                    </ListItemIcon>
                </Tooltip>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <ListItemText
                    primary={
                        <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                            {item.title}
                        </Typography>
                    }
                />
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;